import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Zap, Loader2, Gift, AlertTriangle, ExternalLink, Coins, Shield, Trophy, TrendingUp, TrendingDown } from 'lucide-react';
import { usePriceOracle } from '../providers/PriceOracleProvider';
import SlippageModal from './SlippageModal';

// Wager Economy Constants - 1v1 DUEL
const PLAYER_COUNT = 2;
const DEV_TAX_PERCENT = 10; // 10% Hausgebühr - Treasury behält bei jedem Spiel

const WagerCard = ({ 
  arenaType = 'casual', // 'casual' | 'ranked' | 'highStakes'
  userShards = 0,
  connected = true,
  onPlay,
  onClaimShards,
  claimingShards = false,
  pumpFunUrl = "https://pump.fun"
}) => {
  const { 
    arenaCosts, 
    arenaUsdCosts,
    priceDirection,
    formatPrice,
    tokenPrice,
    lockPriceForTransaction,
    checkSlippage,
    clearLockedPrice,
    updateCount
  } = usePriceOracle();

  const [showSlippageModal, setShowSlippageModal] = useState(false);
  const [slippageData, setSlippageData] = useState(null);
  const [pendingPlay, setPendingPlay] = useState(false);

  // Get entry fee based on arena type (fish, shark, whale)
  const entryFee = arenaCosts[arenaType] || arenaCosts.fish;
  const usdCost = arenaUsdCosts[arenaType] || arenaUsdCosts.fish;
  
  const totalPool = entryFee * PLAYER_COUNT;
  const devTax = Math.floor(totalPool * (DEV_TAX_PERCENT / 100));
  const winnerReward = totalPool - devTax;
  const hasEnoughShards = userShards >= entryFee;

  // Handle play with slippage protection
  const handlePlayClick = useCallback(() => {
    // Lock the current price
    lockPriceForTransaction();
    setPendingPlay(true);
    
    // Small delay to simulate "final check"
    setTimeout(() => {
      const slippage = checkSlippage();
      
      if (slippage.hasSlippage) {
        // Price moved too much - show confirmation modal
        setSlippageData(slippage);
        setShowSlippageModal(true);
        setPendingPlay(false);
      } else {
        // Price is stable - proceed to play
        clearLockedPrice();
        setPendingPlay(false);
        onPlay?.();
      }
    }, 100);
  }, [lockPriceForTransaction, checkSlippage, clearLockedPrice, onPlay]);

  // User confirms new price after slippage
  const handleConfirmNewPrice = useCallback(() => {
    setShowSlippageModal(false);
    setSlippageData(null);
    clearLockedPrice();
    
    // Re-lock at new price and proceed
    lockPriceForTransaction();
    onPlay?.();
  }, [clearLockedPrice, lockPriceForTransaction, onPlay]);

  // User cancels after slippage warning
  const handleCancelSlippage = useCallback(() => {
    setShowSlippageModal(false);
    setSlippageData(null);
    clearLockedPrice();
  }, [clearLockedPrice]);

  // Determine flash color based on price direction
  const getFlashColor = () => {
    if (priceDirection === 'up') return 'shadow-green-500/50';
    if (priceDirection === 'down') return 'shadow-red-500/50';
    return '';
  };

  return (
    <>
      <motion.div 
        className="w-full max-w-md mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="wager-card"
      >
        {/* Main Wager Card */}
        <div className={`bg-black/90 border-2 border-yellow-500/60 rounded-2xl overflow-hidden shadow-2xl shadow-yellow-500/20 transition-shadow duration-300 ${getFlashColor()}`}>
          
          {/* Card Header with LIVE indicator */}
          <div className="bg-gradient-to-r from-yellow-600/30 via-orange-500/30 to-red-500/30 px-4 py-3 border-b border-yellow-500/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Coins className="w-5 h-5 text-yellow-400" />
                <h3 className="font-anton text-xl text-yellow-400 tracking-wider">WAGER DETAILS</h3>
              </div>
              {/* Live Price Indicator */}
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-black/40 border border-cyan-500/30">
                <motion.div
                  className="w-1.5 h-1.5 rounded-full bg-green-400"
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
                <Zap className="w-3 h-3 text-yellow-400" />
                <span className="text-[9px] font-bold text-cyan-400">LIVE</span>
              </div>
            </div>
          </div>

          {/* Card Body */}
          <div className="p-5 space-y-4">
            
            {/* Entry Fee - RED with dynamic update flash */}
            <motion.div 
              className={`flex items-center justify-between p-3 rounded-xl bg-red-500/10 border transition-all duration-300 ${
                priceDirection 
                  ? priceDirection === 'up' 
                    ? 'border-green-500/70 shadow-lg shadow-green-500/30' 
                    : 'border-red-500/70 shadow-lg shadow-red-500/30'
                  : 'border-red-500/40'
              }`}
              key={`entry-${updateCount}`}
              initial={priceDirection ? { scale: 1.02 } : {}}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <span className="text-white/80 font-medium">ENTRY FEE</span>
              </div>
              <div className="flex items-center gap-2">
                <AnimatePresence mode="wait">
                  <motion.span 
                    key={entryFee}
                    className={`font-anton text-2xl transition-colors duration-300 ${
                      priceDirection === 'up' 
                        ? 'text-green-400' 
                        : priceDirection === 'down' 
                          ? 'text-red-400' 
                          : 'text-red-400'
                    }`}
                    initial={{ opacity: 0, y: priceDirection === 'up' ? 10 : -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    data-testid="entry-fee"
                  >
                    {entryFee.toLocaleString()} $SLAP
                  </motion.span>
                </AnimatePresence>
                {priceDirection && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                  >
                    {priceDirection === 'up' ? (
                      <TrendingUp className="w-5 h-5 text-green-400" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-red-400" />
                    )}
                  </motion.div>
                )}
              </div>
            </motion.div>

            {/* USD Value indicator */}
            <div className="text-center text-xs text-white/40">
              ≈ ${usdCost.toFixed(2)} USD @ {formatPrice(tokenPrice)}
            </div>

            {/* Pot Size - GREEN (Large & Prominent) */}
            <motion.div 
              className={`p-4 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-2 transition-all duration-300 ${
                priceDirection 
                  ? priceDirection === 'up' 
                    ? 'border-green-400/80 shadow-lg shadow-green-500/30' 
                    : 'border-red-400/80 shadow-lg shadow-red-500/30'
                  : 'border-green-500/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Trophy className="w-5 h-5 text-green-400" />
                <span className="text-white/80 font-medium uppercase tracking-wide">Winner Takes</span>
              </div>
              <motion.div 
                className="text-center"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <AnimatePresence mode="wait">
                  <motion.span 
                    key={winnerReward}
                    className="font-anton text-5xl md:text-6xl"
                    style={{
                      background: priceDirection === 'down' 
                        ? 'linear-gradient(180deg, #FF6666 0%, #FF3333 50%, #CC0000 100%)'
                        : 'linear-gradient(180deg, #00FF88 0%, #00CC66 50%, #009944 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      filter: `drop-shadow(0 0 20px ${priceDirection === 'down' ? 'rgba(255, 0, 0, 0.4)' : 'rgba(0, 255, 136, 0.4)'})`,
                    }}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                    data-testid="pot-size"
                  >
                    {winnerReward.toLocaleString()} $SLAP
                  </motion.span>
                </AnimatePresence>
              </motion.div>
              <div className="text-center mt-2 text-sm text-white/50">
                1v1 DUEL • Winner Takes All ({totalPool.toLocaleString()} $SLAP)
              </div>
            </motion.div>

            {/* Token Clarification */}
            <div className="text-center text-xs text-white/40 px-4">
              Only <span className="text-cyan-400">$SLAP tokens</span> are transferred.
              Your NFT card stays safe in your wallet.
            </div>
          </div>

          {/* Card Footer - Action Buttons */}
          <div className="p-4 pt-0 space-y-3">
            
            {/* Main Play Button or Insufficient Funds State */}
            {hasEnoughShards ? (
              <motion.div
                animate={!pendingPlay ? { scale: [1, 1.03, 1] } : {}}
                transition={{ duration: 1.2, repeat: Infinity }}
              >
                <Button 
                  onClick={handlePlayClick}
                  disabled={!connected || pendingPlay}
                  className={`w-full h-16 text-black font-anton text-2xl rounded-xl shadow-lg transition-all ${
                    priceDirection === 'up'
                      ? 'bg-gradient-to-r from-green-500 via-emerald-500 to-green-500 shadow-green-500/40'
                      : priceDirection === 'down'
                        ? 'bg-gradient-to-r from-red-500 via-orange-500 to-red-500 shadow-red-500/40'
                        : 'bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 shadow-orange-500/40'
                  } hover:brightness-110`}
                  data-testid="play-button"
                >
                  {!connected ? (
                    <><Loader2 className="w-6 h-6 mr-2 animate-spin" /> CONNECTING...</>
                  ) : pendingPlay ? (
                    <><Loader2 className="w-6 h-6 mr-2 animate-spin" /> CHECKING PRICE...</>
                  ) : (
                    <><Zap className="w-7 h-7 mr-2" /> ENTER ARENA</>
                  )}
                </Button>
              </motion.div>
            ) : (
              <div className="space-y-3">
                {/* Disabled Play Button */}
                <Button 
                  disabled
                  className="w-full h-14 bg-gray-700/50 text-gray-400 font-anton text-xl rounded-xl cursor-not-allowed"
                  data-testid="play-button-disabled"
                >
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  NEED MORE $SLAP
                </Button>

                {/* Buy on Pump.fun Link */}
                <a 
                  href={pumpFunUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full h-12 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-400 hover:to-purple-400 text-white font-anton text-lg rounded-xl transition-all shadow-lg shadow-purple-500/30"
                  data-testid="buy-degen-link"
                >
                  <ExternalLink className="w-5 h-5" />
                  BUY $SLAP ON PUMP.FUN
                </a>

                {/* Dev Tool: Claim Free Shards */}
                <Button
                  onClick={onClaimShards}
                  disabled={claimingShards}
                  className="w-full h-11 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-anton text-base rounded-xl shadow-lg shadow-green-500/20"
                  data-testid="claim-shards-button"
                >
                  {claimingShards ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> CLAIMING...</>
                  ) : (
                    <><Gift className="w-4 h-4 mr-2" /> CLAIM FREE TEST $SLAP</>
                  )}
                </Button>
                <p className="text-xs text-white/40 text-center">Dev Tool: +1000 $SLAP (Testnet Only)</p>
              </div>
            )}

            {/* Balance Display */}
            <div className={`text-center text-sm ${hasEnoughShards ? 'text-white/50' : 'text-red-400'}`}>
              Your Balance: <span className="font-bold">{userShards.toLocaleString()} $SLAP</span>
              {!hasEnoughShards && <span className="ml-2">(Need {(entryFee - userShards).toLocaleString()} more)</span>}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Slippage Protection Modal */}
      <SlippageModal
        isOpen={showSlippageModal}
        onClose={handleCancelSlippage}
        onConfirm={handleConfirmNewPrice}
        slippageData={slippageData}
        formatPrice={formatPrice}
      />
    </>
  );
};

export default WagerCard;
